class VehicleSqFtCalculator {
  constructor(el) {
    this.el = el;
    this.selectedAreas = [];
    this.vehicleClass = '';
    this.showToggle = el.hasAttribute('data-show-toggle');
    this.isSixtyPercent = false;

    // Sheet count (Damplifier products): sq ft covered by one sheet/unit
    this.sheetCoverage = parseFloat(el.dataset.sheetCoverage) || 0;
    this.unitSingular = el.dataset.unitSingular || 'sheet';
    this.unitPlural = el.dataset.unitPlural || 'sheets';

    // Display multiplier: scales the NUMBER shown in the "Amount Needed" readout
    // (not any cart quantity) so mixed container sizes report their amount in one
    // shared base unit. Defaults to 1 when the variant has no
    // custom.square_foot_display_multiplier.
    this.multiplier = parseFloat(el.dataset.sheetMultiplier) || 1;

    // Per-variant sq ft map (variant id -> sq ft per unit), so the readout can
    // recalculate when the shopper switches variant on a product page.
    this.currentVariantId = el.dataset.currentVariant || '';
    this.variantSqFt = {};
    const mapEl = el.querySelector('[data-vehicle-sqft-map]');
    if (mapEl) {
      try {
        this.variantSqFt = JSON.parse(mapEl.textContent) || {};
      } catch (e) {
        this.variantSqFt = {};
      }
    }

    // Per-variant display multiplier map (variant id -> multiplier).
    this.variantMultiplier = {};
    const multEl = el.querySelector('[data-vehicle-multiplier-map]');
    if (multEl) {
      try {
        this.variantMultiplier = JSON.parse(multEl.textContent) || {};
      } catch (e) {
        this.variantMultiplier = {};
      }
    }

    this.vehicleSelect = el.querySelector('[data-vehicle-select]');
    this.calcContent = el.querySelector('[data-calc-content]');
    this.altContent = el.querySelector('[data-alt-content]');
    this.areaBtns = el.querySelectorAll('[data-area]');
    this.valueEl = el.querySelector('[data-sq-ft-value]');
    this.coverageBtns = el.querySelectorAll('[data-coverage]');
    this.sheetResult = el.querySelector('[data-sheet-result]');
    this.sheetCountEl = el.querySelector('[data-sheet-count]');
    this.sheetUnitEl = el.querySelector('[data-sheet-unit]');

    this.vehicleData = {
      '2dcar':      { floor: 20, doors: 12, roof: 14, firewall: 10, trunk: 16, hood: 12, 'wheel-wells': 24, undercarriage: 45 },
      '4dcar':      { floor: 22, doors: 24, roof: 16, firewall: 10, trunk: 18, hood: 12, 'wheel-wells': 24, undercarriage: 50 },
      'midcar':     { floor: 26, doors: 26, roof: 18, firewall: 12, trunk: 22, hood: 14, 'wheel-wells': 24, undercarriage: 55 },
      'fullcar':    { floor: 28, doors: 26, roof: 20, firewall: 12, trunk: 26, hood: 14, 'wheel-wells': 24, undercarriage: 60 },
      '2dtruck':    { floor: 20, doors: 14, roof: 14, firewall: 10, trunk: 0,  hood: 14, 'wheel-wells': 28, undercarriage: 36 },
      '4dtruck':    { floor: 28, doors: 24, roof: 22, firewall: 12, trunk: 0,  hood: 14, 'wheel-wells': 28, undercarriage: 45 },
      'jeep':       { floor: 24, doors: 22, roof: 22, firewall: 10, trunk: 24, hood: 14, 'wheel-wells': 28, undercarriage: 58 },
      'compactSUV': { floor: 28, doors: 26, roof: 24, firewall: 12, trunk: 30, hood: 14, 'wheel-wells': 28, undercarriage: 65 },
      'fullSUV':    { floor: 36, doors: 26, roof: 34, firewall: 14, trunk: 32, hood: 14, 'wheel-wells': 28, undercarriage: 75 },
      'minivan':    { floor: 42, doors: 34, roof: 44, firewall: 14, trunk: 34, hood: 14, 'wheel-wells': 28, undercarriage: 80 },
      'cargovan':   { floor: 60, doors: 34, roof: 60, firewall: 16, trunk: 34, hood: 14, 'wheel-wells': 28, undercarriage: 90 },
    };

    this._bind();
  }

  _bind() {
    if (this.vehicleSelect) {
      this.vehicleSelect.addEventListener('change', () => this._onVehicleChange());
    }

    this.areaBtns.forEach(btn => {
      btn.addEventListener('click', () => this._onAreaToggle(btn));
    });

    this.coverageBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this._setCoverage(parseInt(btn.dataset.coverage, 10));
        this._updateTotal();
      });
    });

    // Recalculate the sheet/unit count when the selected variant changes.
    // Uses the theme's FoxTheme.pubsub variantChange event, same source as
    // buy-buttons-pricing.js. Not scoped to a section id: the calculator is a
    // separate section from the product form, so we match on variant id via
    // our own per-variant map instead.
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        this._onVariantChange(variant ? variant.id : null);
      });
    }
  }

  _onVariantChange(variantId) {
    // Only relevant when this instance carries per-variant sq ft data.
    if (!this.sheetResult) return;

    const id = variantId != null ? String(variantId) : '';
    this.currentVariantId = id;

    const sqft = id && Object.prototype.hasOwnProperty.call(this.variantSqFt, id)
      ? parseFloat(this.variantSqFt[id])
      : 0;
    this.sheetCoverage = isNaN(sqft) ? 0 : sqft;

    const mult = id && Object.prototype.hasOwnProperty.call(this.variantMultiplier, id)
      ? parseFloat(this.variantMultiplier[id])
      : 1;
    this.multiplier = mult > 0 ? mult : 1;

    this._updateTotal();
  }

  _onVehicleChange() {
    this.vehicleClass = this.vehicleSelect.value;
    const isAlt = this.vehicleClass === 'sprinter-van' || this.vehicleClass === 'other';
    const noVehicle = this.vehicleClass === '';

    // Step 2 stays visible. Show the alt message instead of buttons for Sprinter/Other.
    if (this.altContent) this.altContent.classList.toggle('hidden', !isAlt);
    if (this.calcContent) this.calcContent.classList.toggle('hidden', isAlt);

    // Area buttons are present at all times but inert until a real vehicle is chosen.
    const enable = !isAlt && !noVehicle;
    this.areaBtns.forEach(btn => {
      btn.disabled = !enable;
      btn.classList.remove('btn--active');
      btn.setAttribute('aria-pressed', 'false');
    });

    // Reset selections and coverage on every vehicle change.
    this.selectedAreas = [];
    this._setCoverage(100);

    this._updateTotal();
  }

  _onAreaToggle(btn) {
    if (!this.vehicleClass || this.vehicleClass === 'sprinter-van' || this.vehicleClass === 'other') return;

    const area = btn.dataset.area;
    const index = this.selectedAreas.indexOf(area);

    if (index > -1) {
      this.selectedAreas.splice(index, 1);
      btn.classList.remove('btn--active');
      btn.setAttribute('aria-pressed', 'false');
    } else {
      this.selectedAreas.push(area);
      btn.classList.add('btn--active');
      btn.setAttribute('aria-pressed', 'true');
    }

    this._updateTotal();
  }

  _setCoverage(pct) {
    this.isSixtyPercent = pct === 60;
    this.coverageBtns.forEach(btn => {
      const active = parseInt(btn.dataset.coverage, 10) === pct;
      btn.classList.toggle('btn--active', active);
      btn.classList.toggle('toggle-links__btn--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  _updateTotal() {
    if (!this.valueEl) return;

    if (!this.vehicleClass || !this.vehicleData[this.vehicleClass] || this.selectedAreas.length === 0) {
      this.valueEl.textContent = '---';
      this._updateSheetCount(null);
      return;
    }

    const rawTotal = this.selectedAreas.reduce((acc, area) => {
      return acc + (this.vehicleData[this.vehicleClass][area] || 0);
    }, 0);

    const total = this.isSixtyPercent ? Math.round(rawTotal * 0.6) : rawTotal;
    this.valueEl.textContent = total;
    this._updateSheetCount(total);
  }

  _updateSheetCount(total) {
    if (!this.sheetResult) return;

    // No per-unit coverage for the current variant: hide the readout.
    if (!this.sheetCoverage) {
      this.sheetResult.classList.add('hidden');
      return;
    }

    if (total == null || total <= 0) {
      this.sheetResult.classList.add('hidden');
      return;
    }

    const sheets = Math.ceil(total / this.sheetCoverage);
    // displaySheets = amount shown, expressed in the shared base unit. Label only.
    const displaySheets = sheets * this.multiplier;
    if (this.sheetCountEl) this.sheetCountEl.textContent = displaySheets;
    if (this.sheetUnitEl) this.sheetUnitEl.textContent = displaySheets === 1 ? this.unitSingular : this.unitPlural;
    this.sheetResult.classList.remove('hidden');
  }
}

document.querySelectorAll('[data-vehicle-sq-ft-calculator]').forEach(el => {
  new VehicleSqFtCalculator(el);
});
